import { useEffect, useRef, useState } from "react"
import annaAvatarSrc from "./assets/anna-avatar.png"
import clientAvatarSrc from "./assets/client-avatar.png"
import candidateMarkusSrc from "./assets/candidate-markus.png"
import candidateLeaSrc from "./assets/candidate-lea.png"
import candidateJonasSrc from "./assets/candidate-jonas.png"
import styles from "./startups-landing-sections.module.css"

const PHASE_GREETING = 0
const PHASE_TYPE = 1
const PHASE_USER_SHOWN = 2
const PHASE_SEARCH = 3
const PHASE_RESULTS = 4
const PHASE_CLIENT_REPLY = 5
const PHASE_ANNA_CONFIRM = 6

const ANNA_GREETING = "Hey, how can I help you today?"
const USER_PROMPT = "I need a React.js developer, 6+ years, experience in Fintech domain."
const CLIENT_REPLY = "Add all three. Ask each one when they can start."
const ANNA_CONFIRM = "On it. Sending availability questions to all three now."

const GREETING_MS = 2400
const TYPE_DELAY_MS = 1600
const USER_TO_SEARCH_DELAY_MS = 700
const ANNA_TYPING_MS = 4200
const RESULTS_HOLD_MS = 3500
const CLIENT_REPLY_DELAY_MS = 1200
const ANNA_CONFIRM_DELAY_MS = 1000
const FINAL_HOLD_MS = 3000
const RECYCLE_DELAY_MS = 1200

function AnnaFace({ className }) {
	return <img src={annaAvatarSrc} alt="" className={className} draggable={false} />
}

function ClientFace() {
	return <img src={clientAvatarSrc} alt="" className={styles.msgUserFaceImg} draggable={false} />
}

export default function AnnaSourcingDemo() {
	const [phase, setPhase] = useState(PHASE_GREETING)
	const [reducedMotion, setReducedMotion] = useState(false)
	const timeoutsRef = useRef([])

	const clearTimeouts = () => {
		timeoutsRef.current.forEach(clearTimeout)
		timeoutsRef.current = []
	}

	useEffect(() => {
		const mq = window.matchMedia("(prefers-reduced-motion: reduce)")
		setReducedMotion(mq.matches)
		const onChange = () => setReducedMotion(mq.matches)
		mq.addEventListener("change", onChange)
		return () => mq.removeEventListener("change", onChange)
	}, [])

	useEffect(() => {
		if (reducedMotion) setPhase(PHASE_RESULTS)
	}, [reducedMotion])

	/* 0 → 1 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_GREETING) return undefined
		const t = setTimeout(() => setPhase(PHASE_TYPE), GREETING_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 1 → 2 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_TYPE) return undefined
		const t = setTimeout(() => setPhase(PHASE_USER_SHOWN), TYPE_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 2 → 3 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_USER_SHOWN) return undefined
		const t = setTimeout(() => setPhase(PHASE_SEARCH), USER_TO_SEARCH_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 3 → 4 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_SEARCH) return undefined
		const t = setTimeout(() => setPhase(PHASE_RESULTS), ANNA_TYPING_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 4 → 5 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_RESULTS) return undefined
		const t = setTimeout(() => setPhase(PHASE_CLIENT_REPLY), RESULTS_HOLD_MS + CLIENT_REPLY_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 5 → 6 */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_CLIENT_REPLY) return undefined
		const t = setTimeout(() => setPhase(PHASE_ANNA_CONFIRM), ANNA_CONFIRM_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* 6 → recycle */
	useEffect(() => {
		if (reducedMotion || phase !== PHASE_ANNA_CONFIRM) return undefined
		const t = setTimeout(() => {
			clearTimeouts()
			setPhase(PHASE_GREETING)
		}, FINAL_HOLD_MS + RECYCLE_DELAY_MS)
		return () => clearTimeout(t)
	}, [phase, reducedMotion])

	/* Hide early messages once conversation continues past results */
	const hideEarly = phase >= PHASE_CLIENT_REPLY
	const showResults = reducedMotion || phase >= PHASE_RESULTS

	return (
		<div className={styles.annaDemo} aria-hidden="true">
			<div className={styles.annaDemoHeader}>
				<AnnaFace className={styles.annaHeaderFace} />
				<div>
					<div className={styles.annaDemoName}>Anna</div>
					<div className={styles.annaDemoRole}>AI Recruiter</div>
				</div>
			</div>

			<div className={styles.annaMessages}>
				{/* Greeting — hidden when conversation continues */}
				{!hideEarly && (
					<div className={`${styles.msg} ${styles.msgAnna}`}>
						<AnnaFace className={styles.msgAnnaFace} />
						<div className={styles.msgBubble}>{ANNA_GREETING}</div>
					</div>
				)}

				{/* Original user message — hidden when conversation continues */}
				{!hideEarly && phase >= PHASE_USER_SHOWN && (
					<div className={`${styles.msg} ${styles.msgUser}`}>
						<ClientFace />
						<div className={styles.msgBubble}>{USER_PROMPT}</div>
					</div>
				)}

				{/* Searching block */}
				{!reducedMotion && phase === PHASE_SEARCH && (
					<div className={styles.annaSearchingBlock}>
						<AnnaFace className={styles.annaSearchingFace} />
						<div className={styles.annaSearchingBody}>
							<div className={styles.annaSearchingTitle}>Searching Techcore Network</div>
							<div className={styles.typingDots} aria-hidden>
								<span />
								<span />
								<span />
							</div>
						</div>
					</div>
				)}

				{/* Results — stays visible through later phases */}
				{showResults && (
					<div className={`${styles.msg} ${styles.msgAnna}`}>
						<AnnaFace className={styles.msgAnnaFace} />
						<div className={styles.msgBubble}>
							Here are <strong className={styles.strongGold}>3 strong matches</strong> for React + Fintech (6+ yrs):
							<div className={styles.annaResults}>
								<div className={styles.annaResultCard}>
									<img src={candidateMarkusSrc} alt="" className={styles.arcAvatarImg} draggable={false} />
									<div className={styles.arcInfo}>
										<div className={styles.arcName}>Markus R.</div>
										<div className={styles.arcRole}>Senior React · Payments · Remote · 7 yrs</div>
									</div>
								</div>
								<div className={styles.annaResultCard}>
									<img src={candidateLeaSrc} alt="" className={styles.arcAvatarImg} draggable={false} />
									<div className={styles.arcInfo}>
										<div className={styles.arcName}>Lea M.</div>
										<div className={styles.arcRole}>React · Banking APIs · EU · 6 yrs</div>
									</div>
								</div>
								<div className={styles.annaResultCard}>
									<img src={candidateJonasSrc} alt="" className={styles.arcAvatarImg} draggable={false} />
									<div className={styles.arcInfo}>
										<div className={styles.arcName}>Jonas K.</div>
										<div className={styles.arcRole}>Full-Stack · Fintech scale-ups · 8 yrs</div>
									</div>
								</div>
							</div>
							Want me to reach out and add them to your pipeline?
						</div>
					</div>
				)}

				{/* Client follow-up */}
				{phase >= PHASE_CLIENT_REPLY && (
					<div className={`${styles.msg} ${styles.msgUser}`}>
						<ClientFace />
						<div className={styles.msgBubble}>{CLIENT_REPLY}</div>
					</div>
				)}

				{/* Anna confirmation */}
				{phase >= PHASE_ANNA_CONFIRM && (
					<div className={`${styles.msg} ${styles.msgAnna}`}>
						<AnnaFace className={styles.msgAnnaFace} />
						<div className={styles.msgBubble}>{ANNA_CONFIRM}</div>
					</div>
				)}
			</div>
		</div>
	)
}
